// AdminLayout.tsx — layout for the internal ops admin route group (Task 3 of the
// admin-platform cycle, spec docs/superpowers/specs/2026-08-06-admin-platform-design.md §4).
// Guards `/admin/*` on `session.user.role === 'admin'` (the `adminClient()` plugin wired in
// Task 1 augments `useSession`'s `user` with `role`/`banned`/... — see auth-client.ts) and
// renders the Workspaces/Bots/Jobs tab strip (`-mb-px border-b-2`, `no-scrollbar
// overflow-x-auto`) — its own layout, deliberately separate from the dashboard-shell rail
// (dashboard-shell cycle Task 7 deleted the bot pages' equivalent, `BotHeader`; admin keeps
// this idiom on purpose and is out of scope for that cycle).
import { useQuery } from '@tanstack/react-query';
import { NavLink, Navigate, Outlet } from 'react-router';
import type { AdminFeedbackUnhandledCountDto } from '@repo/shared-types';
import { Skeleton, SkeletonText } from '@repo/ui/components/skeleton';
import { apiFetch } from '../lib/api';
import { useSession } from '../lib/auth-client';
import type { Catalog } from '../locales/en';
import { useT } from '../locales/context';

interface Tab {
  to: string;
  label: string;
  /** Only Workspaces needs exact matching — `/admin` is otherwise a path prefix of every
   * other tab. */
  end?: boolean;
}

/** Locale-dashboard plan, Task 4: tab labels moved to `t.shell.*` keys — built from `t` rather
 * than a module-level constant since the tab strip needs the active catalog. */
function tabsFor(t: Catalog): Tab[] {
  return [
    { to: '/admin', label: t.shell.adminTabWorkspaces, end: true },
    { to: '/admin/bots', label: t.shell.adminTabBots },
    { to: '/admin/jobs', label: t.shell.adminTabJobs },
    // Blog/SEO cycle Task 3 (spec docs/superpowers/specs/2026-08-06-blog-seo-design.md §5) —
    // the post editor lives at /admin/posts/:id, a path-prefix of this tab, so it never needs
    // its own `end` flag (same non-`end` treatment as Bots/Jobs above).
    { to: '/admin/posts', label: t.shell.adminTabPosts },
    // Task 4 of the feedback-form cycle (spec docs/superpowers/specs/
    // 2026-08-18-feedback-form-design.md "Dashboard — admin side") — count chip below, sourced
    // from its own cheap query rather than any per-tab `counts` field (the list endpoint has
    // none, see AdminFeedback.tsx's header comment).
    { to: '/admin/feedback', label: t.shell.adminTabFeedback },
    // Task 2 of the playground-analytics cycle (spec .superpowers/sdd/
    // 2026-08-20-playground-analytics/) — demo-widget usage/status overview, no count chip (no
    // "unhandled"-shaped queue here, unlike Feedback above).
    { to: '/admin/playground', label: t.shell.adminTabPlayground },
  ];
}

/** Small count pill for the Feedback tab — same visual formula as the bot rail's `CountPill`
 * (RailNav.tsx: `--st-paused` hue washed to `--st-wash`), reimplemented locally rather than
 * shared because that one is scoped to the bot-nav's icon+label row, not a plain text tab. */
function TabCountPill({ count }: { count: number }) {
  if (count < 1) return null;
  return (
    <span
      className="grid h-[19px] min-w-[19px] shrink-0 place-items-center rounded-full px-[5px] text-[11px] font-semibold text-st-paused"
      style={{
        backgroundColor: 'color-mix(in oklch, var(--st-paused) var(--st-wash), transparent)',
      }}
    >
      {count}
    </span>
  );
}

export function AdminLayout() {
  const { data: session, isPending } = useSession();
  const t = useT();
  const TABS = tabsFor(t);
  // Renders on every admin page, so this stays as cheap as possible: no refetchInterval, a
  // 60s staleTime, and gated off until the session is confirmed admin (avoids an extra 403
  // round-trip while `isPending`/for non-admins who are about to be redirected below).
  const { data: feedbackCount } = useQuery({
    queryKey: ['admin', 'feedback', 'unhandled-count'],
    queryFn: () => apiFetch<AdminFeedbackUnhandledCountDto>('/api/admin/feedback/unhandled-count'),
    enabled: session?.user.role === 'admin',
    staleTime: 60_000,
  });

  // Sized to the real header below it (h1 + tab strip) so nothing shifts once the session
  // resolves — same "one shape per real element" rule as the dashboard shell's skeletons.
  if (isPending) {
    return (
      <div className="flex flex-col gap-6">
        <div className="flex flex-col gap-3">
          <Skeleton className="h-8 w-24" />
          <div className="flex gap-4 border-b pb-2">
            <SkeletonText className="w-20" />
            <SkeletonText className="w-12" />
            <SkeletonText className="w-12" />
            <SkeletonText className="w-14" />
            <SkeletonText className="w-16" />
          </div>
        </div>
      </div>
    );
  }
  if (!session) return <Navigate to="/sign-in" replace />;
  if (session.user.role !== 'admin') return <Navigate to="/" replace />;

  return (
    <div className="flex flex-col gap-6">
      <div className="flex flex-col gap-3">
        <h1 className="text-2xl font-bold">{t.shell.platformAdmin}</h1>
        <div className="no-scrollbar overflow-x-auto border-b">
          <nav className="flex gap-1 whitespace-nowrap">
            {TABS.map((tab) => (
              <NavLink
                key={tab.to}
                to={tab.to}
                end={tab.end}
                className={({ isActive }) =>
                  `-mb-px inline-flex items-center gap-1.5 border-b-2 px-3 py-2 text-sm font-medium whitespace-nowrap transition-colors ${
                    isActive
                      ? 'border-primary text-foreground'
                      : 'border-transparent text-muted-foreground hover:text-foreground'
                  }`
                }
              >
                {tab.label}
                {tab.to === '/admin/feedback' && feedbackCount && (
                  <TabCountPill count={feedbackCount.count} />
                )}
              </NavLink>
            ))}
          </nav>
        </div>
      </div>
      <Outlet />
    </div>
  );
}
